import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { createIdempotencyKey } from "@src/packages/care/cryovault/useSpecimens";
import type { paths } from "@src/packages/openapi/schema";

type FETProcedureBody = NonNullable<
  paths["/patients/{patientId}/frozen-embryo-transfer-plans/{planId}/procedures"]["post"]["requestBody"]
>["content"]["application/json"];
type FETProcedureResponse =
  paths["/patients/{patientId}/frozen-embryo-transfer-plans/{planId}/procedures"]["post"]["responses"]["200"]["content"]["application/json"];
type FETProcedureUpdateBody = NonNullable<
  paths["/patients/{patientId}/frozen-embryo-transfer-plans/{planId}/procedures/{procedureId}"]["patch"]["requestBody"]
>["content"]["application/json"];
type FETProcedureUpdateResponse =
  paths["/patients/{patientId}/frozen-embryo-transfer-plans/{planId}/procedures/{procedureId}"]["patch"]["responses"]["200"]["content"]["application/json"];

type FETSelfReportBody = NonNullable<
  paths["/patients/{patientId}/cycles/{cycleId}/frozen-embryo-transfer-self-report"]["post"]["requestBody"]
>["content"]["application/json"];
type FETSelfReportResponse =
  paths["/patients/{patientId}/cycles/{cycleId}/frozen-embryo-transfer-self-report"]["post"]["responses"]["200"]["content"]["application/json"];

export type FETProcedureReportStatus = NonNullable<FETProcedureBody["status"]>;

export type RecordFETProcedureReportInput = Pick<
  FETProcedureBody,
  "cycleId" | "planVersion" | "status"
> & {
  planId: string;
  appointmentId?: string;
  actualDate?: string;
  scheduledAt?: string | null;
  actualTime?: string;
  actualStartedAt?: string | null;
  actualEndedAt?: string | null;
  actualTransferCount?: number;
  notes?: string;
  reason?: NonNullable<FETProcedureBody["reason"]>;
  clientRequestId?: string;
};

export class FETProcedureReportUnavailableError extends Error {
  constructor() {
    super("The accepted FET plan is unavailable for this procedure report");
    this.name = "FETProcedureReportUnavailableError";
  }
}

export function useRecordFETProcedureReport() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    FETProcedureResponse,
    Error,
    RecordFETProcedureReportInput
  >({
    mutationFn: async ({
      planId,
      cycleId,
      planVersion,
      status,
      appointmentId,
      actualDate,
      scheduledAt,
      actualTime,
      actualStartedAt,
      actualEndedAt,
      actualTransferCount,
      notes,
      reason,
      clientRequestId,
    }) => {
      if (!patientId) {
        throw new Error("Cannot record FET procedure report: missing patient");
      }

      const body: FETProcedureBody = {
        cycleId,
        planVersion,
        status,
        sourceKind: "patient_reported",
        clientRequestId: clientRequestId ?? createIdempotencyKey(),
        ...(appointmentId ? { appointmentId } : {}),
        ...(actualDate ? { actualDate } : {}),
        ...(scheduledAt !== undefined ? { scheduledAt } : {}),
        ...(actualTime ? { actualTime } : {}),
        ...(actualStartedAt !== undefined ? { actualStartedAt } : {}),
        ...(actualEndedAt !== undefined ? { actualEndedAt } : {}),
        ...(actualTransferCount !== undefined ? { actualTransferCount } : {}),
        ...(notes ? { notes } : {}),
        ...(reason ? { reason } : {}),
        ...(status === "cancelled" && !reason ? { reason: "unknown" } : {}),
      };
      const response = await client.POST(
        "/patients/{patientId}/frozen-embryo-transfer-plans/{planId}/procedures",
        {
          params: { path: { patientId, planId } },
          body,
        },
      );
      if (response.error) {
        throwApiError(
          response.error,
          "Failed to record the FET procedure report",
          "fet-procedure-report",
        );
      }
      if (!response.data) {
        throw new Error("FET procedure report response was empty");
      }

      await queryClient.invalidateQueries({
        queryKey: ["fet-plan-for-cycle", patientId, cycleId],
      });
      return response.data;
    },
  });
}

export type ReportFETTransferSelfInput = Pick<
  FETSelfReportBody,
  | "actualDate"
  | "actualTime"
  | "timeZone"
  | "appointmentId"
  | "scheduledAt"
  | "actualTransferCount"
  | "notes"
> & {
  cycleId: string;
  clientRequestId?: string;
};

/**
 * Records the transfer on a cycle no clinic filed a plan for.
 *
 * The whole FET lane hangs off a procedure, and a procedure hangs off an accepted
 * plan snapshotted into the cycle — which acceptance would only grant against a
 * named embryo in cryo storage and a consent reference the patient does not hold.
 * This endpoint stands the chain up on her own account of the transfer instead, in
 * one transaction, and grants nothing over the embryos themselves.
 *
 * It is safe to call when a clinic plan *does* exist: the server puts her report
 * on that contract rather than opening a second plan on the cycle.
 */
export function useReportFETTransferSelf() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    FETSelfReportResponse["data"],
    Error,
    ReportFETTransferSelfInput
  >({
    mutationFn: async ({ cycleId, clientRequestId, ...facts }) => {
      if (!patientId) {
        throw new Error("Cannot report the FET transfer: missing patient");
      }

      const response = await client.POST(
        "/patients/{patientId}/cycles/{cycleId}/frozen-embryo-transfer-self-report",
        {
          params: { path: { patientId, cycleId } },
          body: {
            ...facts,
            clientRequestId: clientRequestId ?? createIdempotencyKey(),
          },
        },
      );
      if (response.error) {
        throwApiError(
          response.error,
          "Failed to record the frozen embryo transfer",
          "fet-transfer-self-report",
        );
      }
      if (!response.data) {
        throw new Error("FET transfer report response was empty");
      }

      // The report may have created the plan this cycle had none of, so the plan
      // and beta lifecycle reads both have to be re-resolved before anything
      // downstream — recording a beta — can find the procedure it needs.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["fet-plan-for-cycle", patientId, cycleId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["fet-beta-lifecycle", patientId, cycleId],
        }),
      ]);
      return response.data.data;
    },
  });
}

export type UpdateFETProcedureReportInput = Pick<
  FETProcedureUpdateBody,
  | "actualDate"
  | "actualTime"
  | "actualTransferCount"
  | "embryoType"
  | "notes"
  | "scheduledAt"
  | "timeZone"
> & {
  planId: string;
  procedureId: string;
  cycleId: string;
  expectedVersion: number;
  clientRequestId?: string;
};

/**
 * Corrects a transfer she had already confirmed.
 *
 * Editing has to be an update, not a second report. A cycle takes one transfer,
 * and nothing in the schema stops a second procedure row being created beside
 * the first — which would leave the hub reading whichever sorted first and the
 * closure waiting on a beta result for a transfer that never separately
 * happened.
 */
export function useUpdateFETProcedureReport() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    FETProcedureUpdateResponse,
    Error,
    UpdateFETProcedureReportInput
  >({
    mutationFn: async ({
      planId,
      procedureId,
      cycleId,
      expectedVersion,
      clientRequestId,
      ...facts
    }) => {
      if (!patientId) {
        throw new Error("Cannot update the FET procedure: missing patient");
      }

      const response = await client.PATCH(
        "/patients/{patientId}/frozen-embryo-transfer-plans/{planId}/procedures/{procedureId}",
        {
          params: { path: { patientId, planId, procedureId } },
          body: {
            ...facts,
            // Sent explicitly because the backend leaves the status alone
            // otherwise (`nextStatus = input.status ?? current.status`). Putting
            // a date on a `scheduled` row without moving it left a transfer that
            // read as reported to the hub and as unreported to the beta-order
            // gate and the closure guard.
            status: "awaiting_report",
            expectedVersion,
            sourceKind: "patient_reported",
            clientRequestId: clientRequestId ?? createIdempotencyKey(),
          },
        },
      );
      if (response.error) {
        throwApiError(
          response.error,
          "Failed to update the FET transfer",
          "fet-procedure-update",
        );
      }
      if (!response.data) {
        throw new Error("FET procedure update response was empty");
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["fet-plan-for-cycle", patientId, cycleId],
        }),
        queryClient.invalidateQueries({
          queryKey: ["fet-beta-lifecycle", patientId, cycleId],
        }),
      ]);
      return response.data;
    },
  });
}
